import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Animated,
  Easing,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import Svg, { Circle } from "react-native-svg";

interface SetupItem {
  id: string;
  label: string;
  badge: string;
  isReady: boolean;
  isLoading: boolean;
}

const INITIAL_ITEMS: SetupItem[] = [
  {
    id: "class-actions",
    label: "Class action claims",
    badge: "Up to $1087",
    isReady: false,
    isLoading: false,
  },
  {
    id: "unclaimed-money",
    label: "Unclaimed money search",
    badge: "$32 on average",
    isReady: false,
    isLoading: false,
  },
  {
    id: "ai-refund",
    label: "AI refund assistant",
    badge: "Up to $31/year",
    isReady: false,
    isLoading: false,
  },
  {
    id: "money-tracking",
    label: "Money tracking",
    badge: "Up to $53/mo",
    isReady: false,
    isLoading: false,
  },
];

const SPIN_DURATION = 1500; // 1.5 seconds per item

// Animated Circle component for progress
const AnimatedCircle = Animated.createAnimatedComponent(Circle);

// Circle progress component
const CircleProgress = ({ progress }: { progress: Animated.Value }) => {
  const size = 24;
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const strokeDashoffset = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [circumference, 0],
  });

  return (
    <Svg width={size} height={size}>
      {/* Background circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#E0E0E0"
        strokeWidth={strokeWidth}
        fill="transparent"
      />
      {/* Progress circle */}
      <AnimatedCircle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#0F9129"
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        rotation="-90"
        origin={`${size / 2}, ${size / 2}`}
      />
    </Svg>
  );
};

const SetupSparrowScreen = () => {
  const [items, setItems] = useState<SetupItem[]>(INITIAL_ITEMS);
  const progressAnims = useRef(INITIAL_ITEMS.map(() => new Animated.Value(0))).current;

  const allReady = items.every((item) => item.isReady);

  useEffect(() => {
    // Start the sequential loading animation
    startLoadingSequence();
  }, []);

  const startLoadingSequence = () => {
    loadNextItem(0);
  };

  const loadNextItem = (index: number) => {
    if (index >= items.length) return;

    // Set current item to loading
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, isLoading: true } : item
      )
    );

    // Start progress animation
    const progressAnim = progressAnims[index];
    progressAnim.setValue(0);

    Animated.timing(progressAnim, {
      toValue: 1,
      duration: SPIN_DURATION,
      easing: Easing.linear,
      useNativeDriver: true,
    }).start(() => {
      // When animation completes, mark as ready and move to next
      setItems((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, isLoading: false, isReady: true } : item
        )
      );

      // Load next item
      loadNextItem(index + 1);
    });
  };

  const handleContinue = () => {
    router.push("/signup/class-actions-select");
  };

  const renderItem = (item: SetupItem, index: number) => {
    const progressAnim = progressAnims[index];
    const isActive = item.isReady || item.isLoading;
    const opacity = isActive ? 1 : 0.5;

    return (
      <View key={item.id} style={styles.itemRow}>
        <View style={styles.itemContent}>
          {/* Icon */}
          <View style={styles.iconContainer}>
            {item.isReady ? (
              <Ionicons name="checkmark-sharp" size={22} color="#0F9129" style={{ fontWeight: 'bold' }} />
            ) : item.isLoading ? (
              <CircleProgress progress={progressAnim} />
            ) : (
              <View style={styles.emptyCircle} />
            )}
          </View>

          {/* Label */}
          <Text style={[styles.itemLabel, { opacity }]}>{item.label}</Text>

          {/* Badge */}
          <View style={[styles.badge, { opacity }]}>
            <Text style={styles.badgeText}>{item.badge}</Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Content */}
        <View style={styles.content}>
          {/* Title and Subtitle */}
          <View style={styles.headerSection}>
            <Text style={styles.title}>Set up your Sparrow AI</Text>
            <Text style={styles.subtitle}>
              In just a few steps, you'll unlock all your benefits and start
              saving money.
            </Text>
          </View>

          {/* Items List */}
          <View style={styles.itemsList}>
            {items.map((item, index) => renderItem(item, index))}
          </View>
        </View>

        {/* Spacer to push footer to bottom */}
        <View style={styles.spacer} />

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <View style={styles.buttonContainer}>
            <ActionButton
              title="Continue"
              onPress={handleContinue}
              disabled={!allReady}
              style={!allReady ? styles.disabledButton : undefined}
            />
          </View>
          <Text style={styles.disclaimer}>*All numbers are estimated</Text>
        </View>
      </SafeAreaView>
    </View>
  );
};

export default SetupSparrowScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  spacer: {
    flex: 1,
  },
  headerSection: {
    gap: 24,
    marginBottom: 24,
    marginTop: 20,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: 0.16,
  },
  itemsList: {
    gap: 20,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  itemContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  iconContainer: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#E0E0E0",
  },
  itemLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  badge: {
    backgroundColor: "#E1EFE4",
    paddingHorizontal: 8,
    borderRadius: 100,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  badgeText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 12,
    lineHeight: 25,
    color: "#161D14",
    letterSpacing: -0.24,
    textAlign: "center",
  },
  bottomSection: {
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    paddingTop: 12,
  },
  buttonContainer: {
    marginBottom: 12,
  },
  disabledButton: {
    opacity: 0.3,
  },
  disclaimer: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "#414141",
    textAlign: "center",
  },
});
